import type { CSSProperties, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { formatNumber } from '@/lib/format'
import { useScrollProgress } from '@/hooks/use-scroll-progress'
import { ArrowLink } from '@/components/generals/cta'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

const ROWS = [
    { label: 'Driftsinntekt', approved: 3095000, other: 393000 },
    { label: 'Driftsresultat', approved: 56000, other: 0 },
    { label: 'Egenkapital', approved: 205000, other: 45000 }
]

const kroner = (value: number): string => `${formatNumber(value)} kr`

/**
 * The approval gap: a thousand dots where 78 light up, then the medians that
 * set the approved firms apart. Each metric has its own scale.
 */
export const ApprovalGap: FC = (): ReactNode => {
    const dots = useRef<HTMLDivElement>(null)
    const bars = useRef<HTMLDivElement>(null)

    useScrollProgress(dots, { startAt: 0.9, endAt: 0.45 })
    useScrollProgress(bars, { startAt: 0.9, endAt: 0.5 })

    return (
        <section id='godkjente-tjener-mer' className='tone-paper relative py-24 md:py-36' data-header='light'>
            <div className='wrap'>
                <div className='grid grid-cols-12 gap-x-6 gap-y-8'>
                    <div className='col-span-12 lg:col-span-8'>
                        <SectionLabel index='01'>Derfor lønner det seg</SectionLabel>
                        <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                            <SplitWords text='Bare 7,8 % er sentralt godkjent. Og de driver større og mer lønnsomt.' />
                        </h2>
                    </div>
                </div>

                <div className='mt-16 grid grid-cols-12 gap-x-6 gap-y-16 md:mt-24'>
                    <figure className='col-span-12 lg:col-span-6'>
                        <div ref={dots} className='sg-dots' role='img' aria-label='1 000 prikker der 78 er oransje: 7,8 % av byggebedriftene er sentralt godkjent'>
                            <div className='sg-dots-field'>
                                <span className='sg-dots-hot sg-dots-hot-a' />
                                <span className='sg-dots-hot sg-dots-hot-b' />
                            </div>
                        </div>

                        <figcaption className='mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-tone-muted'>
                            <span className='flex items-center gap-2'>
                                <span className='size-2.5 rounded-full bg-signal' aria-hidden='true' />
                                Sentralt godkjent, 7,8 %
                            </span>
                            <span className='flex items-center gap-2'>
                                <span className='size-2.5 rounded-full bg-steel' aria-hidden='true' />
                                Ikke godkjent
                            </span>
                            <span className='basis-full text-xs'>Hver prikk er rundt 11 av de 10 752 byggebedriftene med 0–10 ansatte.</span>
                        </figcaption>
                    </figure>

                    <div className='col-span-12 lg:col-span-5 lg:col-start-8'>
                        <div ref={bars} data-bars aria-hidden='true'>
                            {ROWS.map((row, r) => {
                                const max = Math.max(row.approved, row.other) || 1

                                return (
                                    <div key={row.label} className='border-t border-tone-line pt-5 pb-8' style={{ '--s': (r * 0.12).toFixed(2) } as CSSProperties}>
                                        <p className='t-label text-tone-muted'>{row.label} · median</p>

                                        <div className='mt-4 flex flex-col gap-[2px]'>
                                            <div className='sg-bar-row' title={`Sentralt godkjent: ${kroner(row.approved)}`}>
                                                <span className='sg-bar sg-bar-approved' style={{ '--w': row.approved / max } as CSSProperties} />
                                                <span className='sg-bar-value font-semibold'>{kroner(row.approved)}</span>
                                            </div>
                                            <div className='sg-bar-row' title={`Ikke godkjent: ${kroner(row.other)}`}>
                                                <span className='sg-bar sg-bar-other' style={{ '--w': row.other / max } as CSSProperties} />
                                                <span className='sg-bar-value text-tone-muted'>{kroner(row.other)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <table className='sr-only'>
                            <caption>Median for byggebedrifter med 0–10 ansatte, sentralt godkjent mot ikke godkjent</caption>
                            <thead>
                                <tr>
                                    <th scope='col'>Nøkkeltall</th>
                                    <th scope='col'>Sentralt godkjent</th>
                                    <th scope='col'>Ikke godkjent</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ROWS.map(row => (
                                    <tr key={row.label}>
                                        <th scope='row'>{row.label}</th>
                                        <td>{kroner(row.approved)}</td>
                                        <td>{kroner(row.other)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className='flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-tone-line pt-5 text-xs text-tone-muted'>
                            <span className='flex items-center gap-2'><span className='h-2 w-4 bg-signal' aria-hidden='true' /> Sentralt godkjent</span>
                            <span className='flex items-center gap-2'><span className='h-2 w-4 bg-steel' aria-hidden='true' /> Ikke godkjent</span>
                            <span>Hvert nøkkeltall har sin egen skala.</span>
                        </div>

                        <p className='mt-6 text-xs text-tone-muted'>Medianverdier for 10 752 byggebedrifter med 0–10 ansatte.</p>
                        <ArrowLink href='/blogg/hvor-viktig-er-det-med-sentral-godkjenning' className='mt-6'>Les hele analysen</ArrowLink>
                    </div>
                </div>
            </div>
        </section>
    )
}
